/** CSS custom properties for the public website (Django) and the web app (React). */

import {
  type GlassMaterial,
  type Spring,
  type Theme,
  SPACE_STEPS,
  motion,
  radius,
  springs,
  themes,
  typography,
} from "./tokens";

const kebab = (key: string): string => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

export const GLASS_MATERIALS: readonly GlassMaterial[] = ["ultraThin", "thin", "regular", "thick"];

/** The wallpaper as one CSS background (blob 1 follows the agency accent through --ao-accent-glow). */
export function wallpaperBackground(theme: Theme): string {
  const [first, ...others] = theme.wallpaper.blobs;
  const blob = (color: string, b: { x: number; y: number; w: number; h: number }) =>
    `radial-gradient(${b.w}vmax ${b.h}vmax at ${b.x}% ${b.y}%, ${color}, transparent 70%)`;
  return [
    blob("var(--ao-accent-glow)", first),
    ...others.map((b) => blob(b.color, b)),
    `linear-gradient(180deg, ${theme.wallpaper.base[0]}, ${theme.wallpaper.base[1]})`,
  ].join(", ");
}

export function themeVariables(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.color)) vars[`--ao-${kebab(key)}`] = value;
  theme.backdrop.stops.forEach((stop, i) => (vars[`--ao-backdrop-${i + 1}`] = stop));
  theme.backdrop.halos.forEach((halo, i) => (vars[`--ao-halo-${i + 1}`] = halo));
  vars["--ao-wallpaper"] = wallpaperBackground(theme);
  vars["--ao-wallpaper-base"] = theme.wallpaper.base[1];
  for (const level of GLASS_MATERIALS) {
    const glass = theme.glass[level];
    const name = kebab(level);
    vars[`--ao-glass-${name}-bg`] = glass.bg;
    vars[`--ao-glass-${name}-blur`] = `${glass.blur}px`;
    vars[`--ao-glass-${name}-saturate`] = `${glass.saturate}%`;
  }
  vars["--ao-glass-hairline"] = theme.glass.hairline;
  vars["--ao-glass-border-top"] = theme.glass.borderTop;
  vars["--ao-glass-border-bottom"] = theme.glass.borderBottom;
  vars["--ao-glass-highlight"] = theme.glass.highlight;
  vars["--ao-glass-inner-highlight"] = theme.glass.innerHighlight;
  vars["--ao-glass-shadow"] = theme.glass.shadow;
  vars["--ao-glass-opaque-bg"] = theme.glass.opaqueBg;
  for (const [key, value] of Object.entries(theme.soft)) vars[`--ao-soft-${key}`] = value;
  for (const [key, value] of Object.entries(theme.shadow)) vars[`--ao-shadow-${key}`] = value;
  return vars;
}

/**
 * A damped spring turned into a CSS `linear()` easing and its settle duration (ms).
 * Browsers without linear() fall back to --ao-ease-out-expo (see ui.css @supports).
 */
export function springToLinear({ stiffness, damping, mass }: Spring, samples = 32): { easing: string; duration: number } {
  const dt = 1 / 1000;
  let x = 0;
  let v = 0;
  const trace: number[] = [0];
  for (let t = 0; t < 3; t += dt) {
    const a = (-stiffness * (x - 1) - damping * v) / mass;
    v += a * dt;
    x += v * dt;
    trace.push(x);
    if (Math.abs(x - 1) < 0.004 && Math.abs(v) < 0.05 && t > 0.05) break;
  }
  const duration = trace.length - 1;
  const points = Array.from({ length: samples + 1 }, (_, i) => {
    const value = trace[Math.round((i / samples) * duration)] ?? 1;
    return Number(value.toFixed(4));
  });
  points[points.length - 1] = 1;
  return { easing: `linear(${points.join(", ")})`, duration };
}

function sharedVariables(): Record<string, string> {
  const vars: Record<string, string> = {
    "--ao-font-heading": typography.fontHeading,
    "--ao-font-body": typography.fontBody,
    "--ao-font-mono": typography.fontMono,
    "--ao-touch": "44px",
    "--ao-hairline": "0.5px",
    "--ao-duration-instant": `${motion.durationInstant}ms`,
    "--ao-duration-fast": `${motion.durationFast}ms`,
    "--ao-duration-base": `${motion.durationBase}ms`,
    "--ao-duration-slow": `${motion.durationSlow}ms`,
    "--ao-duration-page": `${motion.durationPage}ms`,
    "--ao-duration-theme": `${motion.themeTransition}ms`,
    "--ao-stagger": `${motion.stagger}ms`,
    "--ao-backdrop-cycle": `${motion.backdropCycleSeconds}s`,
    "--ao-ease-out-expo": `cubic-bezier(${motion.easeOutExpo.join(", ")})`,
    "--ao-ease-in-out": `cubic-bezier(${motion.easeInOut.join(", ")})`,
    "--ao-ease-standard": `cubic-bezier(${motion.easeStandard.join(", ")})`,
    "--ao-press-scale": String(motion.pressScale),
  };
  for (const [key, value] of Object.entries(radius)) {
    if (typeof value === "number") vars[`--ao-radius-${key}`] = `${value}px`;
  }
  for (const step of SPACE_STEPS) vars[`--ao-space-${step}`] = `${step * 4}px`;
  for (const [role, s] of Object.entries(typography.web)) {
    const name = kebab(role);
    vars[`--ao-text-${name}-size`] = `${s.size}px`;
    vars[`--ao-text-${name}-line`] = `${s.lineHeight}px`;
    vars[`--ao-text-${name}-weight`] = String(s.weight);
    vars[`--ao-text-${name}-tracking`] = `${s.tracking}em`;
  }
  for (const [name, spring] of Object.entries(springs)) {
    const { easing, duration } = springToLinear(spring);
    vars[`--ao-spring-${name}`] = easing;
    vars[`--ao-spring-${name}-duration`] = `${duration}ms`;
  }
  return vars;
}

const block = (selector: string, vars: Record<string, string>, indent = ""): string =>
  `${indent}${selector} {\n${Object.entries(vars)
    .map(([k, v]) => `${indent}  ${k}: ${v};`)
    .join("\n")}\n${indent}}`;

const OPAQUE_GLASS: Record<string, string> = Object.fromEntries(
  GLASS_MATERIALS.flatMap((level) => [
    [`--ao-glass-${kebab(level)}-bg`, "var(--ao-glass-opaque-bg)"],
    [`--ao-glass-${kebab(level)}-blur`, "0px"],
  ]),
);

const NO_MOTION: Record<string, string> = {
  "--ao-duration-instant": "0ms",
  "--ao-duration-fast": "0ms",
  "--ao-duration-base": "0ms",
  "--ao-duration-slow": "0ms",
  "--ao-duration-page": "0ms",
  "--ao-duration-theme": "0ms",
  "--ao-stagger": "0ms",
  ...Object.fromEntries(Object.keys(springs).map((name) => [`--ao-spring-${name}-duration`, "0ms"])),
};

/** Same specificity as the automatic dark theme selector, declared later: the fallbacks always win. */
const STRONG_ROOT = ":root:not([data-ao-none])";

export function buildCss(): string {
  const dark = { ...themeVariables(themes.dark), "color-scheme": "dark" };
  return [
    "/* Generated by @agencyos/design-tokens — do not edit by hand. */",
    block(":root", { ...sharedVariables(), ...themeVariables(themes.light), "color-scheme": "light" }),
    block('[data-theme="dark"]', dark),
    // A nested light region inside a dark page (theme previews, the /design reference page).
    block(':not(:root)[data-theme="light"]', { ...themeVariables(themes.light), "color-scheme": "light" }),
    `@media (prefers-color-scheme: dark) {\n${block(':root:not([data-theme="light"])', dark, "  ")}\n}`,
    // Opaque glass: reduced transparency, no backdrop-filter support, or .ao-no-glass (slow device, Save-Data).
    `@media (prefers-reduced-transparency: reduce) {\n${block(STRONG_ROOT, OPAQUE_GLASS, "  ")}\n}`,
    `@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {\n${block(STRONG_ROOT, OPAQUE_GLASS, "  ")}\n}`,
    block(":root.ao-no-glass", OPAQUE_GLASS),
    `@media (prefers-reduced-motion: reduce) {\n${block(STRONG_ROOT, NO_MOTION, "  ")}\n}`,
    "",
  ].join("\n\n");
}
